// ============================================
// LIGA AJUPAM - Datos privados de los perfiles (v6.11.213)
// ============================================
// `dni`, `phone` y `birth_date` de `profiles` dejan de leerse directo desde el
// navegador. Hasta v6.11.212 cualquiera con la clave pública de la app podía
// bajarse los tres datos de todos los jugadores. Se cierra en dos pasos:
//
//   1. (v6.11.213) La app lee de `profiles` sólo las columnas públicas
//      (`PROFILE_PUBLIC_COLUMNS`, nunca `*`) y pide los tres datos privados al
//      servidor, que valida quién los pide:
//        - `user_get_private_profiles`  → jugador con sesión: su propio DNI, el
//          teléfono de su compañero/a y de los jugadores de sus ligas (coordinar
//          partidos), la fecha de nacimiento suya y de su compañero/a.
//        - `admin_get_private_profiles` → panel admin: los tres datos de los
//          jugadores de las ligas sobre las que tiene permiso.
//   2. (migración aparte, días después) La base le saca al acceso público el
//      SELECT de esas tres columnas. Desde ahí un `select('*')` sobre
//      `profiles` —directo o embebido (`captain:captain_id(*)`)— FALLA con
//      "permission denied": por eso el audit `audit:profile-columns` lo rechaza.
//
// Si el pedido de datos privados falla (sin sesión, red, etc.) la pantalla
// sigue andando con los datos públicos: nunca se rompe por esto.

import { supabase } from './client';

/**
 * Columnas de `profiles` que cualquiera puede leer. Es TODO menos `dni`,
 * `phone` y `birth_date`. Espejo del GRANT por columna del paso 2: si se agrega
 * una columna pública a `profiles`, va acá y en ese GRANT.
 */
export const PROFILE_PUBLIC_COLUMN_LIST = [
  'id',
  'full_name',
  'instagram',
  'photo_url',
  'category_validated',
  'is_admin',
  'is_blocked',
  'current_team_id',
  'created_at',
  'updated_at',
  'position',
  'dominant_hand',
  'nickname',
  'was_replaced_permanently',
  'last_access',
  'needs_rulebook_reaccept',
  'last_force_reaccept_at',
  'last_force_reaccept_reason',
  'last_force_reaccept_by',
  'sex',
  'shirt_size',
  'shirt_style',
  'city',
  'province',
  'country',
  'simulacro_completed_version',
] as const;

/** Para `.select(...)` y embeds: `captain:captain_id(${PROFILE_PUBLIC_COLUMNS})`. */
export const PROFILE_PUBLIC_COLUMNS = PROFILE_PUBLIC_COLUMN_LIST.join(', ');

/** Los tres datos que sólo da el servidor. */
export interface PrivateProfileFields {
  dni?: number;
  phone?: string;
  birth_date?: string;
}

interface PrivateProfileRow {
  profile_id: string;
  dni: number | null;
  phone: string | null;
  birth_date: string | null;
}

export type PrivateProfileMap = Map<string, PrivateProfileFields>;

const USER_CHUNK = 200; // tope de user_get_private_profiles
const ADMIN_CHUNK = 1000; // tope de admin_get_private_profiles

const uniqueIds = (ids: ReadonlyArray<string | null | undefined>): string[] =>
  Array.from(new Set(ids.filter((id): id is string => typeof id === 'string' && id.length > 0)));

const toMap = (rows: PrivateProfileRow[]): PrivateProfileMap => {
  const map: PrivateProfileMap = new Map();
  for (const row of rows) {
    // Un null del servidor puede ser "no tiene" o "no te corresponde": en los
    // dos casos no hay nada que mostrar, así que no se pisa lo que haya.
    const fields: PrivateProfileFields = {};
    if (row.dni != null) fields.dni = row.dni;
    if (row.phone != null) fields.phone = row.phone;
    if (row.birth_date != null) fields.birth_date = row.birth_date;
    map.set(row.profile_id, fields);
  }
  return map;
};

const fetchChunked = async (
  fnName: 'user_get_private_profiles' | 'admin_get_private_profiles',
  tokenParam: Record<string, string>,
  ids: string[],
  chunk: number
): Promise<PrivateProfileMap> => {
  const rows: PrivateProfileRow[] = [];
  for (let i = 0; i < ids.length; i += chunk) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)(fnName, {
      ...tokenParam,
      p_profile_ids: ids.slice(i, i + chunk),
    });
    if (error) throw error;
    if (Array.isArray(data)) rows.push(...(data as PrivateProfileRow[]));
  }
  return toMap(rows);
};

/**
 * Datos privados que le corresponden al jugador con sesión (ver cabecera).
 * Sin token o ante cualquier error devuelve un mapa vacío.
 */
export const fetchUserPrivateProfiles = async (
  sessionToken: string | null | undefined,
  profileIds: ReadonlyArray<string | null | undefined>
): Promise<PrivateProfileMap> => {
  const ids = uniqueIds(profileIds);
  if (!sessionToken || ids.length === 0) return new Map();
  try {
    return await fetchChunked('user_get_private_profiles', { p_session_token: sessionToken }, ids, USER_CHUNK);
  } catch (error) {
    console.warn('[perfiles] no se pudieron traer los datos privados:', error);
    return new Map();
  }
};

/**
 * Datos privados para el panel admin (sesión de `admin_session_token`).
 * Sin sesión admin o ante cualquier error devuelve un mapa vacío.
 */
export const fetchAdminPrivateProfiles = async (
  profileIds: ReadonlyArray<string | null | undefined>
): Promise<PrivateProfileMap> => {
  const ids = uniqueIds(profileIds);
  let token: string | null = null;
  try {
    token = localStorage.getItem('admin_session_token');
  } catch {
    token = null;
  }
  if (!token || ids.length === 0) return new Map();
  try {
    return await fetchChunked('admin_get_private_profiles', { p_admin_session_token: token }, ids, ADMIN_CHUNK);
  } catch (error) {
    console.warn('[perfiles] no se pudieron traer los datos privados (admin):', error);
    return new Map();
  }
};

/** Suma a un perfil los datos privados que haya en el mapa (si no hay, lo deja igual). */
export const withPrivateFields = <T extends { id?: string | null }>(
  profile: T,
  privateMap: PrivateProfileMap
): T => {
  if (!profile?.id) return profile;
  const fields = privateMap.get(profile.id);
  return fields ? { ...profile, ...fields } : profile;
};

/** Lo mismo para el capitán y el compañero/a de un equipo. */
export const teamWithPrivateFields = <
  T extends { captain?: { id?: string | null } | null; partner?: { id?: string | null } | null }
>(
  team: T,
  privateMap: PrivateProfileMap
): T => ({
  ...team,
  captain: team.captain ? withPrivateFields(team.captain, privateMap) : team.captain,
  partner: team.partner ? withPrivateFields(team.partner, privateMap) : team.partner,
});

/** Ids de capitán y compañero/a de una lista de equipos. */
export const teamPlayerIds = (
  teams: ReadonlyArray<{ captain_id?: string | null; partner_id?: string | null }>
): string[] => uniqueIds(teams.flatMap((t) => [t.captain_id, t.partner_id]));
